import { CreateProductForm } from '@/pages/customer/products'
import axios from '@/services/axios'

const fetchProductsData = async (
	data: FilterPayload,
): Promise<{ data: Product[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/product/get/all?${queryString}`)
	return response.data
}

const fetchProductsByUser = async (
	userId: User['id'],
	data: FilterPayload,
): Promise<{ data: Product[]; pagination: Pagination }> => {
	const queryParams = new URLSearchParams()
	queryParams.append('page', data.pageNumber)
	queryParams.append('limit', data.pageSize)

	if (data.filterColumn && data.filterValue) {
		queryParams.append('filterColumn', data.filterColumn)
		queryParams.append('filterValue', data.filterValue)
	}

	if (data.sortColumn && data.sortType) {
		queryParams.append('sortColumn', data.sortColumn)
		queryParams.append('sortDirection', data.sortType)
	}

	const queryString = queryParams.toString()

	const response = await axios.get(`/product/get-all-by-user-id/${userId}?${queryString}`)
	return response.data
}

const fetchProductsByStand = async (standId: number): Promise<Product[]> => {
	const response = await axios.get(`/product/get-all-by-stand-id/${standId}`)
	return response.data?.data
}

const fetchProductsByBooking = async (
	bookingId: number,
	standId?: number,
	userId?: number,
): Promise<Product[]> => {
	const params = new URLSearchParams()
	if (standId != null) params.append('standId', String(standId))
	if (userId != null) params.append('userId', String(userId))
	const query = params.toString()
	const response = await axios.get(
		`/product/by-booking/${bookingId}${query ? `?${query}` : ''}`,
	)
	return response.data?.data ?? []
}

const fetchProductCategories = async (): Promise<ProductCategory[]> => {
	const response = await axios.get('/category/get/all')
	return response.data?.data
}

const updatePrintStatus = async (productIds: Product['id'][]) => {
	const response = await axios.post(`/product/update-print-status-by-product-ids`, {
		ProductIDs: productIds,
	})
	return response.data
}

const addProduct = async (
	data: CreateProductForm & {
		manufacturer: string
		sku: string
		adaptive_price: string
		manual_price: string
		infinite_inventory: number
		user_id: number
		booking_id: number
	},
) => {
	const response = await axios.post('/product', data)
	return response.data
}

const updateProduct = async (
	productId: Product['id'],
	data: CreateProductForm & {
		manufacturer: string
		sku: string
		adaptive_price: string
		manual_price: string
		infinite_inventory: number
		user_id: number
		booking_id: number
	},
) => {
	const response = await axios.put(`/product/${productId}`, data)
	return response.data
}

const updateNote = async (productId: Product['id'], product_notes: string) => {
	const response = await axios.put(`/product/product-notes/${productId}`, { product_notes })
	return response.data
}

const updatePrice = async (productId: Product['id'], price: number) => {
	const response = await axios.put(`/product/admin/${productId}`, { price })
	return response.data
}

const deleteProduct = async (productId: Product['id']) => {
	const response = await axios.delete(`/product/${productId}`)
	return response.data
}

const addProductsToPos = async () => {
	const response = await axios.get('/product/add/all/user-products-to-pos')
	return response.data
}

const applyDiscount = async (userId: User['id'], data: { discount: string; standId: string[] }) => {
	const response = await axios.post(`/product/apply-discount-by-user-and-stand-id/${userId}`, data)
	return response.data
}

const searchProducts = async (
	searchValue: string,
	selectedColumn: string,
	page: number,
	limit: number,
): Promise<{
	status: string
	message: string
	data: ProductSearch[]
	pagination: Pagination
}> => {
	const params: Record<string, any> = {
		page,
		limit,
	}
	params[selectedColumn] = searchValue

	const response = await axios.get(`/product/public/search`, {
		params,
	})

	return response.data
}

const productService = {
	addProduct,
	updateNote,
	applyDiscount,
	addProductsToPos,
	searchProducts,
	fetchProductCategories,
	fetchProductsByStand,
	fetchProductsByBooking,
	fetchProductsData,
	updatePrintStatus,
	updatePrice,
	updateProduct,
	deleteProduct,
	fetchProductsByUser,
}

export default productService
